import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import MiniPopup from '../../../common/MiniPopup';
import { ApprovePayment } from './PayoutsModal/ApprovePayment';
import { EditPayoutTable, RemarkForm } from './EditPayout/EditPayoutTable';
import { openCustomModal, openFullscreenModal } from '../../../actions/modal';
import { cleanupTicketDetails, cleanupTicketPaymentDetails } from '../../../actions/tickets';
import ButtonScogoClosedOutlined from '../../../common/Buttons/ButtonScogoClosedOutlined';
import Form from '../../../common/Form';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import TicketBankTransferPayment from './EditPayout/TicketBankTransferPayment';
import { approveTicketMilestone, fixRma, getServiceRequestPayments } from '../../../actions/service_request';
import { canApproveSignOffTicket, canBankTransferTicket, isIndividualMilestoneStatusInRange, isMilestoneStatusInRange, MilestoneSubStatus, MilestoneType, TicketAgentRole, TicketStatus } from '../../Ticket/TicketController';
import { TransactionStatus } from '../PaymentController';
import ApproveSignOff from './EditPayout/ApproveSignOff';
import { isPavMilestoneSignOffUnlocked } from '../../../features/pav/pavConfig';
import { ScogoID } from '../../../utils/common';
import { motion } from 'framer-motion';
import Radio from '../../../common/Form/Radio';
import UpdateRmaDetails from '../../Ticket/Modal/UpdateRmaDetails';
import CustomerApproveRejectSignOff from '../../Ticket/Modal/CustomerApproveRejectSignOff';

const formName = 'editPayout';
const arrayName = 'editPayout';

const EditPayout = (props) => {
    const navigate = useNavigate();
    let { serviceRequest, showAdditionalAmountApprove, isServiceParnter = false, onCancel = () => navigate(-1) } = props;
    const dispatch = useDispatch();
    const formDivRef = useRef(null);
    const approveRef = useRef();
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);

    // Check if RMA component is visible
    const hasRMAMilestones = useMemo(() => {
        return serviceRequest?.milestones?.some(
            milestone => milestone.type === MilestoneType.RMA &&
                milestone.milestone_status !== MilestoneSubStatus.HARD_CLOSED
        ) || false;
    }, [serviceRequest?.milestones]);

    useEffect(() => {
        if (serviceRequest?.id) {
            let roles = serviceRequest.agents.filter(agent => agent.agent.id === newLoggedUserData.user.id).map(agent => agent.role);
            dispatch(getServiceRequestPayments({ ticketId: serviceRequest.id, payload: {}, roles, org_id: serviceRequest.helpdesk_id }));
        }
    }, [dispatch, serviceRequest?.id, newLoggedUserData.user.id, serviceRequest?.agents, serviceRequest?.helpdesk_id]);

    const [isEditingRow, setIsEditingRow] = useState([]);
    const [approvePaymentPopUpData, setApprovePaymentPopUpData] = useState({});
    const [rowPaymentsData, setRowPaymentsData] = useState([]);
    const [selectedAgentFilter, setSelectedAgentFilter] = useState(null);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);

    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);

    const defaultValues = { [arrayName]: [] };
    const methods = useForm({ defaultValues, mode: 'all' });
    const { setValue } = methods;

    const conditionToCheck = useMemo(() => {
        let isCustomerApproval = false;
        let isApproveSignOff = false;
        let customerApprovalMilestones = [];
        let approveSignOffMilestones = [];
        const org_membership_ids = newLoggedUserData.orgMemberships.map(({ id }) => id);
        const milestonesToCheck = serviceRequest.milestones ? serviceRequest.milestones.filter((milestone) => ![MilestoneSubStatus.SIGN_OFF_REJECTED, MilestoneSubStatus.HARD_CLOSED].includes(milestone.milestone_status) && (milestone.is_signoff_locked === false || isPavMilestoneSignOffUnlocked(milestone))) : [];

        if (serviceRequest.helpdesk_id === ScogoID && serviceRequest.org_id === newLoggedUserData.org.id && serviceRequest.is_customer_sign_off_required === true && canApproveSignOffTicket(serviceRequest.actions) && ![TicketStatus.CLOSED, TicketStatus.ON_HOLD].includes(serviceRequest.status)) {
            isCustomerApproval = true;
            customerApprovalMilestones = milestonesToCheck.filter((milestone) => milestone.milestone_status === MilestoneSubStatus.SIGN_OFF_REQUESTED);
        }
        if (org_membership_ids.includes(serviceRequest.helpdesk_id) && canApproveSignOffTicket(serviceRequest.actions) && ![TicketStatus.CLOSED, TicketStatus.ON_HOLD].includes(serviceRequest.status)) {
            isApproveSignOff = true;
            if (serviceRequest.is_customer_sign_off_required === true && serviceRequest.helpdesk_id === ScogoID) {
                approveSignOffMilestones = milestonesToCheck.filter((milestone) => milestone.milestone_status === MilestoneSubStatus.CUSTOMER_SIGN_OFF_ACCEPTED);
            } else {
                approveSignOffMilestones = milestonesToCheck.filter((milestone) => milestone.milestone_status === MilestoneSubStatus.SIGN_OFF_REQUESTED);
            }
        }

        return { isCustomerApproval, isApproveSignOff, customerApprovalMilestones, approveSignOffMilestones };
    }, [serviceRequest, newLoggedUserData.org.id, newLoggedUserData.orgMemberships]);

    const agents = useMemo(() => {
        let scm = [], cluster = [], psp = [], supervisor = [], warehouseOwner = [], sp = [];
        let customer = {
            role: TicketAgentRole.CUSTOMER,
            label: 'Customer',
            agent: {
                id: serviceRequest?.organization?.id,
                display_name: serviceRequest?.organization?.name,
            }
        };
        let list = [];

        const isCustomer = serviceRequest.helpdesk_id === newLoggedUserData.org.id || serviceRequest.org_id !== ScogoID;

        // A Warehouse Owner is only relevant to RMA milestones. Ignore any WO whose milestone_id points to a
        // non-RMA milestone. (Only applied when milestone data is available, otherwise we can't determine the type.)
        const rmaMilestoneIds = new Set((serviceRequest?.milestones || []).filter((m) => m.type === MilestoneType.RMA).map((m) => m.id));
        const hasMilestones = Array.isArray(serviceRequest?.milestones) && serviceRequest.milestones.length > 0;
        const isNonRmaWarehouseOwner = (a) => hasMilestones && a.milestone_id != null && !rmaMilestoneIds.has(a.milestone_id);

        if (serviceRequest?.agents?.length > 0) {
            serviceRequest.agents.forEach(agent => {
                if (agent.role === TicketAgentRole.SCM) scm.push({ ...agent, label: 'SCM' });
                if (agent.role === TicketAgentRole.CLUSTER && cluster.length === 0) cluster.push({ ...agent, label: 'Cluster' });
                if (agent.role === TicketAgentRole.PSP && psp.length === 0) psp.push({ ...agent, label: 'PSP' });
                if (agent.role === TicketAgentRole.SUPERVISOR && supervisor.length === 0) supervisor.push({ ...agent, label: 'Supervisor' });
                if (agent.role === TicketAgentRole.WAREHOUSE_OWNER && warehouseOwner.length === 0 && !isNonRmaWarehouseOwner(agent)) warehouseOwner.push({ ...agent, label: 'Warehouse Owner' });
            });
        };


        if (serviceRequest?.milestones?.length > 0 && serviceRequest?.agents?.length > 0) {
            const isRmaTicket = serviceRequest.milestones.find((elem) => elem.type === MilestoneType.RMA);

            const withRma = serviceRequest.milestones.filter(
                (mile) => mile.type === MilestoneType.RMA
            );
            const withoutRma = serviceRequest.milestones.filter(
                (mile) => mile.type !== MilestoneType.RMA
            );

            serviceRequest.milestones = [...withRma, ...withoutRma];

            serviceRequest.milestones.forEach((mile) => {
                const findCurrentSp = serviceRequest.agents.find((agent) => agent.role === TicketAgentRole.SP && mile.id === agent.milestone_id);

                if (!findCurrentSp) return;

                if (isRmaTicket && mile.type === MilestoneType.LOGISTICS) {
                    const isSpAlreadyExist = sp.find((s) => s.agent.id === findCurrentSp.agent.id);
                    if (!isSpAlreadyExist) {
                        sp.push({ ...findCurrentSp, label: 'Partner', milestone_ids: [mile.id] });
                    } else {
                        isSpAlreadyExist.milestone_ids = [isSpAlreadyExist.milestone_id, mile.id];
                    }
                } else {
                    sp.push({ ...findCurrentSp, label: 'Partner' })
                }
            })
        }

        if (scm && scm.find(item => item.agent.id === newLoggedUserData.user.id)) {
            list = [customer, ...sp, ...cluster, ...psp, ...supervisor, ...warehouseOwner].filter(Boolean);
        } else if (cluster && cluster.find(item => item.agent.id === newLoggedUserData.user.id)) {
            list = [...sp, ...cluster, ...psp, ...supervisor].filter(Boolean);
        } else if (psp && psp.find(item => item.agent.id === newLoggedUserData.user.id)) {
            list = [...sp, ...psp, ...supervisor].filter(Boolean);
        } else if (supervisor && supervisor.find(item => item.agent.id === newLoggedUserData.user.id)) {
            list = [...sp, ...supervisor].filter(Boolean);
        } else if (sp && sp.find(item => item.agent.id === newLoggedUserData.user.id)) {
            const findSp = sp.find(item => item.agent.id === newLoggedUserData.user.id);
            list = [findSp].filter(Boolean);
        } else if (isCustomer) {
            list = [...sp].filter(Boolean);
        }

        const isSelfWarehouseOwner = warehouseOwner && warehouseOwner.find(item => item.agent.id === newLoggedUserData.user.id);
        if (isSelfWarehouseOwner && warehouseOwner) {
            list.push(...warehouseOwner);
        };

        const paymentRowData = list.filter((l) => l.role !== TicketAgentRole.CUSTOMER);
        setRowPaymentsData(paymentRowData);
        return list;
    }, [serviceRequest, newLoggedUserData.user.id, newLoggedUserData.org.id]);

    const agentFilterOptions = useMemo(() => {
        return agents
            .filter(({ role }) => role !== TicketAgentRole.CUSTOMER)
            .map(({ agent, role, milestone_id }) => ({
                key: `${agent.id}_${role}_${milestone_id || ''}`,
                label: `${agent.display_name} (${role?.replace(/_/g, ' ')})`,
                value: `${agent.id}_${role}_${milestone_id || ''}`,
                agentId: agent.id,
                role,
                milestone_id,
            }));
    }, [agents]);

    const statusFilterOptions = useMemo(() => [
        { key: TransactionStatus.PENDING, label: 'Pending', value: TransactionStatus.PENDING },
        { key: TransactionStatus.SCHEDULED, label: 'Scheduled', value: TransactionStatus.SCHEDULED },
        { key: TransactionStatus.INITIATED, label: 'Initiated', value: TransactionStatus.INITIATED },
        { key: TransactionStatus.PROCESSING, label: 'Processing', value: TransactionStatus.PROCESSING },
        { key: TransactionStatus.SUCCESS, label: 'Success', value: TransactionStatus.SUCCESS },
        { key: TransactionStatus.FAILURE, label: 'Failure', value: TransactionStatus.FAILURE },
        { key: TransactionStatus.REJECTED, label: 'Rejected', value: TransactionStatus.REJECTED },
        { key: TransactionStatus.HOLD, label: 'Hold', value: TransactionStatus.HOLD },
    ], []);

    const hasActiveFilters = selectedAgentFilter || selectedStatusFilter;

    useEffect(() => {
        return () => {
            dispatch(cleanupTicketDetails());
            dispatch(cleanupTicketPaymentDetails());
            setValue(arrayName, []);
        };
    }, [dispatch, setValue]);


    const onSignOffApproveClick = () => {
        return dispatch(
            openCustomModal({
                heading: 'Approve SignOff',
                modalHeight: 'auto',
                modalWidth: '40rem',
                modalComponent: <ApproveSignOff milestones={conditionToCheck.approveSignOffMilestones} org_id={serviceRequest.org_id} ticket_id={serviceRequest.id} agents={serviceRequest.agents} />
            })
        )
    };

    const onCustomerSignOffApproveClick = () => {
        return dispatch(
            openCustomModal({
                heading: 'Approve SignOff',
                modalHeight: 'auto',
                modalWidth: '40rem',
                modalComponent: <CustomerApproveRejectSignOff ticket={serviceRequest} milestones={conditionToCheck.customerApprovalMilestones} status={MilestoneSubStatus.CUSTOMER_SIGN_OFF_ACCEPTED} />
            })
        )
    };

    return (
        <div className='bg-gradient-to-br from-white to-gray-50 w-full approvePOContainer shadow-2xl border border-gray-100 relative flex flex-col overflow-hidden backdrop-blur-sm'
            style={{
                height: 'calc(100vh - 12rem)',
            }}>
            <RMADetailsAndSPUpdate serviceRequest={serviceRequest} />
            {/* Header Section with Filters */}
            <div className='flex-shrink-0 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200' >
                <div className='px-4 py-2'>
                    <div className='flex items-center justify-between gap-3 flex-wrap'>
                        <div className='flex items-center gap-1 text-md text-blue-600 bg-blue-100 px-2 py-1 rounded-full flex-shrink-0'>
                            <span className='material-icons text-xs'>info</span>
                            <p className='font-medium'>All payments include TDS and System Charges deduction</p>
                        </div>
                        <div className='flex items-center gap-2 flex-shrink-0'>
                            {agentFilterOptions.length > 0 && (
                                <select
                                    value={selectedAgentFilter?.value || ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (!val) {
                                            setSelectedAgentFilter(null);
                                        } else {
                                            const found = agentFilterOptions.find((opt) => opt.value === val);
                                            setSelectedAgentFilter(found || null);
                                        }
                                    }}
                                    className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 bg-white text-gray-700 min-w-[150px]"
                                >
                                    <option value="">All Agents</option>
                                    {agentFilterOptions.map((opt) => (
                                        <option key={opt.key} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                            <select
                                value={selectedStatusFilter || ''}
                                onChange={(e) => {
                                    setSelectedStatusFilter(e.target.value || null);
                                }}
                                className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 bg-white text-gray-700 min-w-[130px]"
                            >
                                <option value="">All Statuses</option>
                                {statusFilterOptions.map((opt) => (
                                    <option key={opt.key} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                            {hasActiveFilters && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedAgentFilter(null);
                                        setSelectedStatusFilter(null);
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5 px-2 py-1 rounded hover:bg-blue-50 transition-colors border border-blue-200"
                                >
                                    <span className="material-icons" style={{ fontSize: '12px' }}>close</span>
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Area - Takes remaining space */}
            <div className='flex-1 min-h-0 overflow-hidden' ref={formDivRef}>
                <Form
                    methods={methods}
                    className='flex flex-col h-full'
                    submit={(e) => { }}
                    customFormButtons={true}
                    isLoading={formButtonLoading[formName]}
                >
                    <div className='flex flex-col h-full min-h-0'>
                        <div className='flex-1 min-h-0 overflow-hidden'>
                            <EditPayoutTable
                                isEditingRow={isEditingRow}
                                showAdditionalAmountApprove={showAdditionalAmountApprove}
                                setIsEditingRow={setIsEditingRow}
                                ticketId={serviceRequest.id}
                                isServiceParnter={isServiceParnter}
                                arrayName={arrayName}
                                serviceRequest={serviceRequest}
                                agents={agents}
                                hasRMAMilestones={hasRMAMilestones}
                                selectedAgentFilter={selectedAgentFilter}
                                selectedStatusFilter={selectedStatusFilter}
                            />
                        </div>
                    </div>
                </Form>
                <div className='flex items-center gap-2 relative '>
                    <MiniPopup
                        handleClick={() => { }}
                        popup={<ApprovePayment scogo_transaction_no={approvePaymentPopUpData?.scogo_transaction_no} scheduled_on={approvePaymentPopUpData?.scheduled_on} />}
                        transition={false}
                        className='fixed px-3 py-2 mt-2 z-10 rounded-md shadow-lg bg-white overflow-scroll left-0 top-0'
                        menuStyle={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
                    >
                        <div ref={approveRef} />
                    </MiniPopup>
                </div>
            </div>

            {/* Enhanced Footer Section - Always visible at bottom */}
            <div className='flex-shrink-0 approvePOButtons flex gap-2 items-center bg-gradient-to-r from-gray-50 to-white px-4 py-3 border-t border-gray-200 rounded-b-2xl justify-end shadow-lg sticky bottom-0 z-50'
                style={{
                    minHeight: '60px',
                    backgroundColor: '#f8f9fa',
                    border: '2px solid #e9ecef'
                }}>
                <div className='flex items-center gap-2'>
                    {canBankTransferTicket(serviceRequest) && (
                        <div className='transform transition-all duration-200 hover:scale-105'>
                            <TicketBankTransferPayment serviceRequest={serviceRequest} rowPaymentsData={rowPaymentsData} />
                        </div>
                    )}
                    {(conditionToCheck.isApproveSignOff && conditionToCheck.approveSignOffMilestones.length > 0) && (
                        <div className='transform transition-all duration-200 hover:scale-105 approve_signoff_button'>
                            <ButtonScogoPrimary
                                onClick={onSignOffApproveClick}
                                textOrComponent={
                                    <div className='flex items-center gap-2'>
                                        <span className='material-icons text-sm'>check_circle</span>
                                        <span>Approve SignOff</span>
                                    </div>
                                }
                            />
                        </div>
                    )}
                    {(conditionToCheck.isCustomerApproval && conditionToCheck.customerApprovalMilestones.length > 0) && (
                        <div className='transform transition-all duration-200 hover:scale-105 customer_approval_button'>
                            <ButtonScogoPrimary
                                onClick={onCustomerSignOffApproveClick}
                                textOrComponent={
                                    <div className='flex items-center gap-2'>
                                        <span className='material-icons text-sm'>check_circle</span>
                                        <span>Approve SignOff</span>
                                    </div>
                                }
                            />
                        </div>
                    )}
                    <div className='transform transition-all duration-200 hover:scale-105'>
                        <ButtonScogoClosedOutlined
                            textOrComponent={
                                <div className='flex items-center gap-2'>
                                    <span className='material-icons text-sm'>close</span>
                                    <span>Cancel</span>
                                </div>
                            }
                            onClick={onCancel}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditPayout;

export const getApproveSignOffTicketPayload = ({ data, isCustomer, isWarehouseOwner, isPsp, isCLuster, isSuperVisor }) => {
    let payload = {};
    data.forEach((item) => {
        if (item?.referenceId && item.type === 'Additional Payment') {
            if (isCustomer) {
                payload[`customer_amount_${item.referenceId}`] = item.customer_details;
                payload[`customer_transaction_id_${item.referenceId}`] = item.customer_beneficiary_transaction_id;
            }

            if (isWarehouseOwner) {
                payload[`warehouse_amount_${item.referenceId}`] = item.warehouse_details;
                payload[`warehouse_transaction_id_${item.referenceId}`] = item.warehouse_owner_beneficiary_transaction_id;
            }

            if (isPsp) {
                payload[`psp_amount_${item.referenceId}`] = item.psp_details;
                payload[`psp_transaction_id_${item.referenceId}`] = item.psp_beneficiary_transaction_id;
            }
            if (isCLuster) {
                payload[`cluster_amount_${item.referenceId}`] = item.cluster_details;
                payload[`cluster_transaction_id_${item.referenceId}`] = item.cluster_beneficiary_transaction_id;
            }

            if (isSuperVisor) {
                payload[`supervisor_amount_${item.referenceId}`] = item.supervisor_details;
                payload[`supervisor_transaction_id_${item.referenceId}`] = item.supervisor_beneficiary_transaction_id;
            }

            payload[`category_${item.referenceId}`] = item.category;
            payload[`remark_${item.referenceId}`] = item.remark;
            payload[`proof_${item.referenceId}`] = item.proof;
            payload[`partner_amount_${item.referenceId}`] = item.partner_details;
            payload[`partner_transaction_id_${item.referenceId}`] = item.partner_beneficiary_transaction_id;
        }
        if (item.type === 'Base Payment') {
            if (isCustomer) payload.customer_base_payment = item.customer_details;
            if (isWarehouseOwner) payload.warehouse_base_payment = item.warehouse_details;
            if (isPsp) payload.psp_base_payment = item.psp_details;
            if (isCLuster) payload.cluster_base_payment = item.cluster_details;
            if (isSuperVisor) payload.supervisor_base_payment = item.supervisor_details;
            payload.partner_base_payment = item.partner_details;
        }
    });

    return payload;
};



export const RenderApproveSignOff = ({ children, className, selectedMilestone, ticketId, formName, onSuccess, category = 'SIGN_OFF', org_id }) => {
    const dispatch = useDispatch();

    const openRemarkPopup = ({ formName, onClick }) => {
        dispatch(
            openCustomModal({
                heading: 'Remark',
                modalHeight: 'auto',
                modalWidth: '40rem',
                modalComponent: <RemarkForm formName={formName} onClick={onClick} category={category} org_id={org_id} />
            })
        )
    }

    const onSignOffApproveClick = () => {
        openRemarkPopup({
            formName,
            onClick: ({ processed_remark }) => {
                let p = {};
                if (processed_remark?.value) {
                    p.accepted_remark = processed_remark.label;
                    if (!processed_remark.value.startsWith('create_this_issue')) {
                        p.master_remark_id = processed_remark.value;
                    }
                }
                dispatch(approveTicketMilestone({
                    payload: p,
                    formName,
                    ticketId,
                    milestoneId: selectedMilestone,
                    onSuccess
                }));
            }
        });
    };

    return <div className={`${className}`} onClick={onSignOffApproveClick}>
        {children}
    </div>
}



// RMA Details and SP Update Component
const RMADetailsAndSPUpdate = ({ serviceRequest }) => {
    const dispatch = useDispatch();
    const { formButtonLoading } = useSelector((state) => state.loading);

    const submitButtonRef = useRef(null);
    const rmaMilestones = serviceRequest?.milestones?.filter(milestone => milestone.type === MilestoneType.RMA && milestone.milestone_status !== MilestoneSubStatus.HARD_CLOSED) || [];

    const [selectedRMAIndex, setSelectedRMAIndex] = useState(0);
    const selectedRMAMilestone = rmaMilestones[selectedRMAIndex];
    const [initialValues, setInitialValues] = useState({ asset_consumed: null, faulty_device_picked_up: null });

    const getRMADetails = () => {
        if (!selectedRMAMilestone) return null;
        const findAsset = serviceRequest?.assets?.find(asset => asset.asset_id === selectedRMAMilestone?.rma?.spare_asset_id);
        const findFaultyAsset = serviceRequest?.assets?.find(asset => asset.asset_id === selectedRMAMilestone?.rma?.faulty_asset_id);
        return {
            status: selectedRMAMilestone?.rma?.status || 'pending',
            faultyAsset: findFaultyAsset?.asset?.serial_number || selectedRMAMilestone?.rma?.faulty_asset_id || '',
            spareAsset: findAsset?.asset?.serial_number || selectedRMAMilestone?.rma?.spare_asset_id || '',
            consumptionType: selectedRMAMilestone?.rma?.consumption_type === 'none' ? 'No device consumed' : selectedRMAMilestone?.rma?.consumption_type || '',
            asset_consumed: selectedRMAMilestone?.rma?.asset_consumed,
            faulty_device_picked_up: selectedRMAMilestone?.rma?.faulty_device_picked_up
        };
    };

    const rmaDetails = getRMADetails();

    const defaultValues = {
        asset_consumed: '',
        faulty_device: ''
    };

    const methods = useForm({ defaultValues, mode: 'all' });
    const watchedValues = methods.watch(['asset_consumed', 'faulty_device_picked_up']);

    useEffect(() => {
        if (rmaDetails) {
            let asset_consumed_value = null;
            let faulty_device_picked_up_value = null;

            if (rmaDetails.asset_consumed !== undefined && rmaDetails.asset_consumed !== null) {
                asset_consumed_value = rmaDetails.asset_consumed ? 'yes' : 'no';
                methods.setValue('asset_consumed', asset_consumed_value);
            } else {
                methods.setValue('asset_consumed', null);
            }

            if (rmaDetails.faulty_device_picked_up !== undefined && rmaDetails.faulty_device_picked_up !== null) {
                faulty_device_picked_up_value = rmaDetails.faulty_device_picked_up ? 'yes' : 'no';
                methods.setValue('faulty_device_picked_up', faulty_device_picked_up_value);
            } else {
                methods.setValue('faulty_device_picked_up', null);
            }

            // Store initial values for comparison
            setInitialValues({
                asset_consumed: asset_consumed_value,
                faulty_device_picked_up: faulty_device_picked_up_value
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRMAIndex, selectedRMAMilestone?.id]);

    // Check if values have changed from initial values
    const hasValuesChanged = useMemo(() => {
        const currentAssetConsumed = watchedValues[0];
        const currentFaultyDevice = watchedValues[1];

        // Edge case: If both current and initial are null/empty, no change
        if ((!currentAssetConsumed || currentAssetConsumed === '') &&
            (!currentFaultyDevice || currentFaultyDevice === '') &&
            !initialValues.asset_consumed &&
            !initialValues.faulty_device_picked_up) {
            return false;
        }

        // Check if at least one value has changed
        const assetConsumedChanged = currentAssetConsumed !== initialValues.asset_consumed &&
            currentAssetConsumed !== null &&
            currentAssetConsumed !== '';

        const faultyDeviceChanged = currentFaultyDevice !== initialValues.faulty_device_picked_up &&
            currentFaultyDevice !== null &&
            currentFaultyDevice !== '';

        return assetConsumedChanged || faultyDeviceChanged;
    }, [watchedValues, initialValues]);

    const onSubmit = (formValues) => {
        let payload = {};
        if (formValues.asset_consumed) payload.asset_consumed = formValues.asset_consumed === 'yes' ? true : false;
        if (formValues.faulty_device_picked_up) payload.faulty_device_picked_up = formValues.faulty_device_picked_up === 'yes' ? true : false;
        dispatch(fixRma({
            id: serviceRequest.id,
            milestoneId: selectedRMAMilestone.id,
            formName: 'sp_update',
            payload
        }));
    };

    if (rmaMilestones.length === 0) {
        return null;
    }

    return (
        <div className="mb-2 space-y-4">
            {/* RMA Milestone Selector - Show only if multiple RMA milestones */}
            {rmaMilestones.length > 1 && (
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 px-4 py-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="material-icons text-purple-600">swap_horiz</span>
                            <h3 className="text-md font-semibold text-gray-800">Select RMA Milestone</h3>
                        </div>
                        <div className="flex gap-2">
                            {rmaMilestones.map((milestone, index) => (
                                <button
                                    key={milestone.id}
                                    onClick={() => setSelectedRMAIndex(index)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${selectedRMAIndex === index
                                        ? 'bg-purple-500 text-white shadow-lg'
                                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                        }`}
                                >
                                    RMA {index + 1}
                                    {milestone.milestone_status && (
                                        <span className="ml-2 text-xs opacity-75">
                                            ({milestone.milestone_status})
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* RMA Details Card */}
                {selectedRMAMilestone && (
                    <motion.div
                        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-4 py-3 border-b border-purple-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="material-icons text-purple-600 text-lg">build_circle</span>
                                    <h3 className="text-md font-semibold text-purple-800">RMA Details</h3>
                                </div>
                                {rmaMilestones.length > 1 && (
                                    <div className="bg-purple-200 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                                        RMA {selectedRMAIndex + 1} of {rmaMilestones.length}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-4 space-y-3">
                            {/* RMA Status */}
                            <div className='flex gap-4 w-full'>
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2">
                                        <span className="material-icons text-gray-600 text-sm">info</span>
                                        <span className="text-md font-medium text-gray-700">RMA Status:</span>
                                    </div>
                                    <div className={`px-2 py-1 rounded-full text-md font-medium ${rmaDetails.status === 'completed' ? 'bg-green-100 text-green-800' :
                                        rmaDetails.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                                            rmaDetails.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-gray-100 text-gray-800'
                                        }`}>
                                        {rmaDetails.status.replace(/_/g, ' ')}
                                    </div>
                                </div>

                                {/* Faulty Asset */}
                                {rmaDetails.faultyAsset && <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2">
                                        <span className="material-icons text-red-600 text-sm">error</span>
                                        <span className="text-md font-medium text-gray-700">Faulty Asset:</span>
                                    </div>
                                    <span className="text-md text-gray-600 font-medium">{rmaDetails.faultyAsset}</span>
                                </div>}
                            </div>

                            <hr className="border-t border-gray-200 my-2" />

                            {/* Spare Asset */}
                            <div className='flex gap-4 w-full'>
                                {/* Consumption Type */}
                                {rmaDetails.consumptionType && <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2">
                                        <span className="material-icons text-blue-600 text-sm">category</span>
                                        <span className="text-md font-medium text-gray-700">Consumption Type:</span>
                                    </div>
                                    <div className="text-md text-gray-600 font-medium">
                                        {rmaDetails.consumptionType}
                                    </div>
                                </div>}

                                {rmaDetails.spareAsset && rmaDetails.consumptionType === 'full' ? <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2">
                                        <span className="material-icons text-green-600 text-sm">inventory</span>
                                        <span className="text-md font-medium text-gray-700">Spare Asset:</span>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-md text-gray-600 font-medium">{rmaDetails.spareAsset}</div>
                                    </div>
                                </div> : null}
                            </div>
                        </div>
                    </motion.div>
                )}

                <motion.div
                    className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                >
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-blue-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 w-full">
                                <span className="material-icons text-blue-600 text-lg">person</span>
                                <h3 className="text-md font-semibold text-blue-800">SP Update</h3>

                                {selectedRMAMilestone && rmaDetails.status === 'pending' && (
                                    <div className="flex items-center gap-2 ml-auto">
                                        <div className="flex items-center gap-2">
                                            <span className="material-icons text-amber-600 text-sm">warning</span>
                                            <span className="text-md font-medium text-amber-700">RMA update is required for this milestone</span>
                                        </div>
                                        <button
                                            onClick={() => {
                                                dispatch(
                                                    openFullscreenModal({
                                                        heading: (
                                                            <span className='text-black'>
                                                                {'Update RMA Details'} :&nbsp;<span className='text-scogobgsky text-font16'>{serviceRequest.ticket_number}</span>
                                                            </span>
                                                        ),
                                                        modalWidth: '70rem',
                                                        modalHeight: 'auto',
                                                        modalComponent: <UpdateRmaDetails id={serviceRequest.id} source='edit_payout' />,
                                                    })
                                                );
                                            }}
                                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1 transition-colors duration-200"
                                        >
                                            <span className="material-icons text-xs">update</span>
                                            <span>Update RMA</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="p-4">
                        <Form
                            submitRef={submitButtonRef}
                            methods={methods}
                            onSubmit={onSubmit}
                            submitButtonText={
                                <div className="flex items-center gap-2">
                                    <span className="material-icons text-sm">update</span>
                                    <span>Update Status</span>
                                </div>
                            }
                            showSubmit={false}
                            submitContainerClassName='flex justify-end mt-4'
                            showCancelButton={false}
                        >
                            <div className="flex gap-4 w-full">
                                {/* Asset Consumed */}
                                <div className="bg-green-50 rounded-lg border-2 border-green-200 p-3 flex items-center gap-2 w-1/2">
                                    <div className="flex items-center gap-2">
                                        <span className="material-icons text-green-600 text-sm">check_circle</span>
                                        <label className="text-md font-medium text-green-800">Asset Consumed</label>
                                    </div>
                                    <Radio
                                        name='asset_consumed'
                                        className='flex gap-4'
                                        options={[
                                            { label: 'Yes', value: 'yes' },
                                            { label: 'No', value: 'no' }
                                        ]}
                                    />
                                </div>

                                {/* Faulty Device */}
                                <div className="bg-red-50 rounded-lg border-2 border-red-200 p-3 flex items-center gap-2 w-1/2">
                                    <div className="flex items-center gap-2">
                                        <span className="material-icons text-red-600 text-sm">build</span>
                                        <label className="text-md font-medium text-red-800">Faulty Device Picked Up</label>
                                    </div>
                                    <Radio
                                        name='faulty_device_picked_up'
                                        className='flex gap-4'
                                        options={[
                                            { label: 'Yes', value: 'yes' },
                                            { label: 'No', value: 'no' }
                                        ]}
                                    />
                                </div>
                                <motion.button
                                    id='update_rma_status'
                                    type="button"
                                    className={`flex items-center justify-center gap-2 px-4 py-2 font-medium rounded-lg shadow-md transition-all duration-300 border-2 h-12 my-auto w-36 ${!hasValuesChanged || formButtonLoading['sp_update']
                                        ? 'bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed'
                                        : 'bg-orange-400 hover:bg-orange-500 text-white border-orange-300 hover:shadow-lg'
                                        }`}
                                    onClick={() => methods.handleSubmit(onSubmit)()}
                                    disabled={!hasValuesChanged || formButtonLoading['sp_update']}
                                    whileHover={hasValuesChanged && !formButtonLoading['sp_update'] ? { scale: 1.05, boxShadow: "0px 5px 15px rgba(237, 137, 54, 0.4)" } : {}}
                                    whileTap={hasValuesChanged && !formButtonLoading['sp_update'] ? { scale: 0.95 } : {}}
                                >
                                    {formButtonLoading['sp_update'] ? (
                                        <div className="flex justify-center items-center">
                                            <div className="spinner-border animate-spin inline-block w-5 h-5 border-3 rounded-full border-t-transparent" role="status" />
                                        </div>
                                    ) : (
                                        <>
                                            <span className="material-icons">update</span>
                                            <span>Update</span>
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </Form>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};
